// Creative Strategist Plan tab (Task #2378, spec §51).
//
// This is the approval gate the user sees. Everything above the buttons is a
// PROPOSAL — reading it costs nothing and builds nothing. Nothing expensive
// dispatches until Build This Plan or Build Selected is pressed.
//
// The tab is deliberately explicit about three things the spec cares about:
//   * WHY each item is here (its dependency state — already exists, you
//     supplied it, we build it from what you said, we work it out);
//   * WHETHER the tool and template it needs are actually available;
//   * WHAT the user can do about anything that is blocked, rather than the
//     plan silently routing around a gate.

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest, queryClient as globalQueryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Loader2,
  Lock,
  Map as MapIcon,
  Pencil,
  RefreshCw,
  Sparkles,
  Trash2,
  Undo2,
  X,
} from "lucide-react";
import {
  CS_DEPENDENCY_STATUS_CLASSES,
  CS_DEPENDENCY_STATUS_LABELS,
  CS_EXECUTOR_LABELS,
  CS_PLAN_GROUPS,
  CS_PLAN_GROUP_LABELS,
  csPlanKey,
  csTasksKey,
  type CreativeStrategistPlanBlocker,
  type CreativeStrategistDependency,
  type CreativeStrategistPlanGroup,
  type CreativeStrategistPlanItem,
  type CreativeStrategistPlanState,
} from "@/lib/creative-strategist";

interface PlanTabProps {
  projectId: string;
  onApproved?: () => void;
}

export function CreativeStrategistPlanTab({ projectId, onApproved }: PlanTabProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, { title: string; taskCount: number }>>({});
  const [removed, setRemoved] = useState<Record<string, boolean>>({});
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  const planKey = csPlanKey(projectId);
  const { data, isLoading } = useQuery<CreativeStrategistPlanState>({
    queryKey: [planKey],
    enabled: !!projectId,
    // Poll only while a resolver run is actually in flight.
    refetchInterval: (query) =>
      (query.state.data as CreativeStrategistPlanState | undefined)?.resolutionStatus ===
      "running"
        ? 3000
        : false,
  });

  const plan = data?.plan ?? null;
  const resolution = data?.resolution ?? null;
  const approved = data?.planStatus === "approved";
  const running = data?.resolutionStatus === "running";

  const liveItems = useMemo(
    () => (plan?.items ?? []).filter((i) => !i.removed),
    [plan],
  );

  // Reset the local edit buffer whenever a new proposal arrives.
  useEffect(() => {
    setEditing(false);
    setDrafts({});
    setRemoved({});
    setSelected({});
  }, [data?.planVersion, projectId]);

  const resolveMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/creative-strategist/projects/${projectId}/resolve`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [planKey] });
      toast({
        title: "Working out what this needs",
        description: "Reading your brand's personas, products, offers and angles.",
      });
    },
    onError: (e: any) =>
      toast({ title: "Could not start", description: e?.message, variant: "destructive" }),
  });

  const editMutation = useMutation({
    mutationFn: async () => {
      const items = liveItems
        .map((item) => {
          const draft = drafts[item.key];
          const isRemoved = removed[item.key] === true;
          if (!draft && !isRemoved) return null;
          return {
            key: item.key,
            ...(draft && draft.title !== item.title ? { title: draft.title } : {}),
            ...(draft && draft.taskCount !== item.taskCount
              ? { taskCount: draft.taskCount }
              : {}),
            ...(isRemoved ? { removed: true } : {}),
          };
        })
        .filter(Boolean);
      if (items.length === 0) return null;
      return apiRequest("PATCH", planKey, { items });
    },
    onSuccess: () => {
      setEditing(false);
      setDrafts({});
      setRemoved({});
      queryClient.invalidateQueries({ queryKey: [planKey] });
    },
    onError: (e: any) =>
      toast({ title: "Could not save", description: e?.message, variant: "destructive" }),
  });

  const approveMutation = useMutation({
    mutationFn: async (itemKeys: string[] | null) =>
      apiRequest("POST", `${planKey}/approve`, itemKeys ? { itemKeys } : {}),
    onSuccess: async (res: any) => {
      const body = await res?.json?.().catch(() => null);
      queryClient.invalidateQueries({ queryKey: [planKey] });
      queryClient.invalidateQueries({ queryKey: [csTasksKey(projectId)] });
      globalQueryClient.invalidateQueries({
        queryKey: [`/api/creative-strategist/projects/${projectId}`],
      });
      toast({
        title: body?.created ? "Plan approved" : "Already approved",
        description: body?.taskCount
          ? `${body.taskCount} ${body.taskCount === 1 ? "task" : "tasks"} are ready to run.`
          : undefined,
      });
      onApproved?.();
    },
    onError: (e: any) =>
      toast({ title: "Could not approve", description: e?.message, variant: "destructive" }),
  });

  if (isLoading) {
    return (
      <div className="space-y-4" data-testid="plan-loading">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!plan) {
    return (
      <Card>
        <CardContent className="py-14 text-center space-y-3" data-testid="empty-plan">
          <MapIcon className="h-6 w-6 mx-auto text-muted-foreground" />
          <div className="text-sm font-medium">
            {running ? "Working out what this needs…" : "No plan yet"}
          </div>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {data?.resolutionError ??
              (running
                ? "Reading your brand's personas, products, offers and saved angles."
                : "Ask for what you want in the chat, or work out a plan from the objective.")}
          </p>
          {!running && (
            <Button
              onClick={() => resolveMutation.mutate()}
              disabled={resolveMutation.isPending}
              data-testid="button-build-plan-proposal"
            >
              {resolveMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4 mr-2" />
              )}
              Work out the plan
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  const selectedKeys = Object.entries(selected)
    .filter(([, v]) => v)
    .map(([k]) => k);
  const anySelected = selectedKeys.length > 0;
  const buildable = liveItems.filter((i) => i.status === "planned" && !removed[i.key]);

  return (
    <div className="space-y-5" data-testid="plan-tab">
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 space-y-1">
              <h2 className="text-lg font-semibold" data-testid="text-plan-title">
                {plan.planTitle}
              </h2>
              <p className="text-sm text-muted-foreground" data-testid="text-plan-summary">
                {plan.summary}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {approved ? (
                <Badge
                  variant="secondary"
                  className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300"
                  data-testid="badge-plan-approved"
                >
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Approved
                </Badge>
              ) : (
                <Badge variant="secondary" data-testid="badge-plan-proposed">
                  Proposed
                </Badge>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => resolveMutation.mutate()}
                disabled={resolveMutation.isPending || running}
                data-testid="button-rerun-resolution"
                title="Re-read the brand and refresh this proposal"
              >
                <RefreshCw
                  className={`h-3.5 w-3.5 ${running || resolveMutation.isPending ? "animate-spin" : ""}`}
                />
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
            <span data-testid="text-plan-task-count">
              {plan.estimatedTasks} {plan.estimatedTasks === 1 ? "task" : "tasks"}
            </span>
            <span data-testid="text-plan-deliverable-count">
              {plan.estimatedDeliverables}{" "}
              {plan.estimatedDeliverables === 1 ? "deliverable" : "deliverables"}
            </span>
            {plan.discoveryRequired ? (
              <span>Discovery needed for at least one input</span>
            ) : (
              <span>No discovery needed — using what you already have</span>
            )}
          </div>
        </CardContent>
      </Card>

      {resolution && resolution.dependencies.length > 0 && (
        <DependencyStrip dependencies={resolution.dependencies} />
      )}

      {plan.blockers.length > 0 && <BlockerList blockers={plan.blockers} />}

      {CS_PLAN_GROUPS.map((group) => {
        const items = liveItems.filter((i) => i.group === group);
        if (items.length === 0) return null;
        const groupSummary = plan.groups.find((g) => g.group === group)?.summary ?? "";
        return (
          <PlanGroupCard
            key={group}
            group={group}
            summary={groupSummary}
            items={items}
            editing={editing}
            approved={approved}
            drafts={drafts}
            removed={removed}
            selected={selected}
            onDraft={(key, patch) =>
              setDrafts((prev) => ({
                ...prev,
                [key]: {
                  title: patch.title ?? prev[key]?.title ?? "",
                  taskCount: patch.taskCount ?? prev[key]?.taskCount ?? 1,
                },
              }))
            }
            onToggleRemove={(key) =>
              setRemoved((prev) => ({ ...prev, [key]: !prev[key] }))
            }
            onToggleSelect={(key) =>
              setSelected((prev) => ({ ...prev, [key]: !prev[key] }))
            }
          />
        );
      })}

      {!approved && (
        <Card className="sticky bottom-4 shadow-lg">
          <CardContent className="p-4 flex flex-wrap items-center gap-2 justify-between">
            <div className="text-xs text-muted-foreground">
              {buildable.length === 0
                ? "Nothing here can be built yet — resolve the blockers above."
                : anySelected
                  ? `${selectedKeys.length} of ${buildable.length} selected`
                  : "Nothing runs until you approve this plan."}
            </div>
            <div className="flex flex-wrap gap-2">
              {editing ? (
                <>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditing(false);
                      setDrafts({});
                      setRemoved({});
                    }}
                    data-testid="button-cancel-edit-plan"
                  >
                    <X className="h-3.5 w-3.5 mr-1.5" /> Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => editMutation.mutate()}
                    disabled={editMutation.isPending}
                    data-testid="button-save-plan-edits"
                  >
                    {editMutation.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <Check className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    Save changes
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditing(true)}
                    data-testid="button-edit-plan"
                  >
                    <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit Plan
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => approveMutation.mutate(selectedKeys)}
                    disabled={!anySelected || approveMutation.isPending}
                    data-testid="button-build-selected"
                  >
                    Build Selected
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => approveMutation.mutate(null)}
                    disabled={buildable.length === 0 || approveMutation.isPending}
                    data-testid="button-build-this-plan"
                  >
                    {approveMutation.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    Build This Plan
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/** The "why is this here" strip — one badge per prerequisite. */
function DependencyStrip({
  dependencies,
}: {
  dependencies: CreativeStrategistDependency[];
}) {
  return (
    <Card>
      <CardContent className="p-5 space-y-3">
        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          What this needs
        </div>
        <div className="space-y-2">
          {dependencies.map((dep) => (
            <div
              key={dep.key}
              className="flex items-start gap-3 text-sm"
              data-testid={`dependency-${dep.key}`}
            >
              <Badge
                variant="secondary"
                className={`shrink-0 ${CS_DEPENDENCY_STATUS_CLASSES[dep.status] ?? ""}`}
                data-testid={`dependency-status-${dep.key}`}
              >
                {CS_DEPENDENCY_STATUS_LABELS[dep.status] ?? dep.status}
              </Badge>
              <div className="min-w-0">
                <div className="font-medium">
                  {dep.label}
                  {dep.name ? <span className="text-muted-foreground"> · {dep.name}</span> : null}
                </div>
                <div className="text-xs text-muted-foreground">{dep.reason}</div>
                {dep.question ? (
                  <div className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                    {dep.question}
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/** Spec §43/§36 — a gated tool is shown with its options, never bypassed. */
function BlockerList({ blockers }: { blockers: CreativeStrategistPlanBlocker[] }) {
  return (
    <Card className="border-amber-300 dark:border-amber-800">
      <CardContent className="p-5 space-y-4" data-testid="plan-blockers">
        <div className="flex items-center gap-2 text-sm font-medium">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          {blockers.length} {blockers.length === 1 ? "blocker" : "blockers"}
        </div>
        {blockers.map((blocker) => (
          <div key={blocker.id} className="space-y-2" data-testid={`blocker-${blocker.id}`}>
            <div>
              <div className="text-sm font-medium">{blocker.title}</div>
              <div className="text-xs text-muted-foreground">{blocker.reason}</div>
            </div>
            <div className="grid gap-1.5 sm:grid-cols-2">
              {blocker.options.map((option) => (
                <div
                  key={option.key}
                  className="rounded-md border p-2"
                  data-testid={`blocker-option-${blocker.id}-${option.key}`}
                >
                  <div className="text-xs font-medium">{option.label}</div>
                  <div className="text-xs text-muted-foreground">{option.description}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function PlanGroupCard({
  group,
  summary,
  items,
  editing,
  approved,
  drafts,
  removed,
  selected,
  onDraft,
  onToggleRemove,
  onToggleSelect,
}: {
  group: CreativeStrategistPlanGroup;
  summary: string;
  items: CreativeStrategistPlanItem[];
  editing: boolean;
  approved: boolean;
  drafts: Record<string, { title: string; taskCount: number }>;
  removed: Record<string, boolean>;
  selected: Record<string, boolean>;
  onDraft: (key: string, patch: { title?: string; taskCount?: number }) => void;
  onToggleRemove: (key: string) => void;
  onToggleSelect: (key: string) => void;
}) {
  return (
    <Card data-testid={`plan-group-${group}`}>
      <CardContent className="p-5 space-y-3">
        <div>
          <div className="text-sm font-semibold">{CS_PLAN_GROUP_LABELS[group]}</div>
          {summary ? (
            <p className="text-xs text-muted-foreground mt-0.5">{summary}</p>
          ) : null}
        </div>
        <Separator />
        <div className="space-y-3">
          {[...items]
            .sort((a, b) => a.order - b.order)
            .map((item) => {
              const isRemoved = removed[item.key] === true;
              const draft = drafts[item.key];
              const blocked = item.status === "blocked";
              return (
                <div
                  key={item.key}
                  className={`flex items-start gap-3 ${isRemoved ? "opacity-50" : ""}`}
                  data-testid={`plan-item-${item.key}`}
                >
                  {!editing && !approved && !blocked && (
                    <Checkbox
                      className="mt-1"
                      checked={selected[item.key] === true}
                      onCheckedChange={() => onToggleSelect(item.key)}
                      data-testid={`checkbox-plan-item-${item.key}`}
                    />
                  )}
                  <div className="min-w-0 flex-1 space-y-1">
                    {editing && !blocked ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <Input
                          className="h-8 max-w-md"
                          value={draft?.title ?? item.title}
                          onChange={(e) => onDraft(item.key, { title: e.target.value })}
                          data-testid={`input-plan-item-title-${item.key}`}
                        />
                        <Input
                          type="number"
                          min={1}
                          className="h-8 w-20"
                          value={draft?.taskCount ?? item.taskCount}
                          onChange={(e) =>
                            onDraft(item.key, { taskCount: Number(e.target.value) })
                          }
                          data-testid={`input-plan-item-count-${item.key}`}
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onToggleRemove(item.key)}
                          data-testid={`button-remove-plan-item-${item.key}`}
                        >
                          {isRemoved ? (
                            <Undo2 className="h-3.5 w-3.5" />
                          ) : (
                            <Trash2 className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium">{item.title}</span>
                        {blocked ? (
                          <Badge
                            variant="secondary"
                            className="bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300"
                            data-testid={`badge-plan-item-blocked-${item.key}`}
                          >
                            <Lock className="h-3 w-3 mr-1" /> Blocked
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">
                            {item.taskCount} {item.taskCount === 1 ? "task" : "tasks"}
                          </Badge>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">{item.description}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                      <span data-testid={`plan-item-dependency-state-${item.key}`}>
                        {item.dependencyState}
                      </span>
                      {item.templateAvailability ? (
                        <span data-testid={`plan-item-template-${item.key}`}>
                          {item.templateAvailability}
                        </span>
                      ) : null}
                      {item.requiredAccess ? (
                        <span data-testid={`plan-item-access-${item.key}`}>
                          {item.requiredAccess}
                        </span>
                      ) : null}
                      <span>
                        {CS_EXECUTOR_LABELS[item.executor] ?? item.executor}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </CardContent>
    </Card>
  );
}
